import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import '../styles/NewProject.css';
import { v4 as uuidv4 } from 'uuid';
import API_BASE from '../utils/api.js';
import { validateWebImageFile } from '../utils/imageUpload.js';
import TagSelector from '../components/shared/TagSelector';
import AddressAutocomplete from '../components/common/AddressAutocomplete';

const VISUALLY_HIDDEN_FILE_INPUT_STYLE = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function authHeaders() {
  const idToken = localStorage.getItem('idToken');
  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
}

const initialForm = {
  projectNickname: '',
  customerId: '',
  customerName: '',
  email: '',
  secondaryEmail: '',
  phone: '',
  secondaryPhone: '',
  billingStreet: '',
  billingStreet2: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  projectStreet: '',
  projectStreet2: '',
  projectCity: '',
  projectState: '',
  projectZip: '',
  notes: '',
  coverImageUrl: '',
  pinterestBoardUrl: '',
  receivingCompany: '',
  receivingStreet: '',
  receivingCity: '',
  receivingState: '',
  receivingZip: '',
  receivingEmail: '',
  receivingPhone: '',
  quickbooksCustomer: '',
  paymentMethod: '',
  defaultMarkup: '',
  salesTaxRate: '',
  timeBillingRoleRates: [],
  documents: [],
  isActive: true,
};

const NEW_SALES_TAX_VALUE = '__new_sales_tax__';
const NEW_QB_CUSTOMER_VALUE = '__new_qb_customer__';

function normalizeRoleRateEntry(entry) {
  return {
    role: String(entry?.role || '').trim(),
    rate: entry?.rate === '' || entry?.rate == null ? '' : String(entry.rate),
  };
}

function normalizeRoleRateEntries(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.map(normalizeRoleRateEntry).filter((entry) => entry.role || entry.rate !== '');
}

const US_STATE_OPTIONS = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
];

function Modal({ isOpen, onClose, title, children }) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="np-modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="np-modal-box">
        <div className="np-modal-header">
          <h3>{title}</h3>
          <button aria-label="Close" className="np-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="np-modal-body">{children}</div>
      </div>
    </div>
  );
}

export default function NewProject() {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isEdit = Boolean(projectId);
  const existingProject = location.state?.project || null;

  const [form, setForm] = useState(initialForm);
  const [tags, setTags] = useState([]);

  const [status, setStatus] = useState({ loading: false, error: null, success: null });
  const apiBase = API_BASE;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [custFilter, setCustFilter] = useState('');
  const containerRef = useRef(null);

  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [modalData, setModalData] = useState({
    company: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    billingStreet: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    secFirstName: '',
    secLastName: '',
    secEmail: '',
    secPhone: '',
    secBillingStreet: '',
    secBillingCity: '',
    secBillingState: '',
    secBillingZip: '',
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const coverUploadRef = useRef(null);
  const [uploadingDocs, setUploadingDocs] = useState([]);
  const [uploadingCover, setUploadingCover] = useState(null);
  const [showCoverPrompt, setShowCoverPrompt] = useState(false);
  const [coverDragActive, setCoverDragActive] = useState(false);

  const [qbStatus, setQbStatus] = useState({ connected: false });
  const [qbCustomers, setQbCustomers] = useState([]);
  const [qbLoading, setQbLoading] = useState(false);
  const [newQbCustomerName, setNewQbCustomerName] = useState('');
  const [savingNewQbCustomer, setSavingNewQbCustomer] = useState(false);

  const [taxRates, setTaxRates] = useState([]);
  const [taxLoading, setTaxLoading] = useState(false);
  const [newSalesTax, setNewSalesTax] = useState({
    city: '',
    state: '',
    tax: '',
    agency: '',
    updated: '',
  });
  const [savingNewSalesTax, setSavingNewSalesTax] = useState(false);
  const [teamRoles, setTeamRoles] = useState([]);
  const coverInputId = 'new-project-cover-input';

  useEffect(() => {
    if (!isEdit || !existingProject) return;

    setForm((prev) => ({
      ...prev,
      projectNickname: existingProject.projectNickname || '',
      customerId: existingProject.customerId || '',
      customerName: existingProject.customerName || '',
      email: existingProject.email || '',
      phone: existingProject.phone || '',
      billingStreet: existingProject.billingStreet || '',
      billingStreet2: existingProject.billingStreet2 || '',
      billingCity: existingProject.billingCity || '',
      billingState: existingProject.billingState || '',
      billingZip: existingProject.billingZip || '',
      projectStreet: existingProject.projectStreet || '',
      projectStreet2: existingProject.projectStreet2 || '',
      projectCity: existingProject.projectCity || '',
      projectState: existingProject.projectState || '',
      projectZip: existingProject.projectZip || '',
      receivingCity: existingProject.receivingCity || '',
      receivingEmail: existingProject.receivingEmail || '',
      receivingCompany: existingProject.receivingCompany || '',
      receivingPhone: existingProject.receivingPhone || '',
      receivingStreet: existingProject.receivingStreet || '',
      receivingState: existingProject.receivingState || '',
      receivingZip: existingProject.receivingZip || '',
      notes: existingProject.notes || '',
      documents: Array.isArray(existingProject.documents) ? existingProject.documents : [],
      coverImageUrl: existingProject.coverImageUrl || '',
      defaultMarkup: existingProject.defaultMarkup || '',
      paymentMethod: existingProject.paymentMethod || '',
      quickbooksCustomer: existingProject.quickbooksCustomer || '',
      salesTaxRate: existingProject.salesTaxRate || '',
      timeBillingRoleRates: normalizeRoleRateEntries(existingProject.timeBillingRoleRates),
      secondaryEmail: existingProject.secondaryEmail || '',
      secondaryPhone: existingProject.secondaryPhone || '',

      isActive: existingProject.isActive ?? true,
    }));
    if (existingProject.tags) setTags(existingProject.tags);
  }, [isEdit, existingProject]);

  useEffect(() => {
    async function loadCustomers() {
      try {
        const userId = localStorage.getItem('userId') || '';
        const res = await fetch(`${apiBase}/api/customers?userId=${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error('customers endpoint not available');
        const json = await res.json();
        setCustomers(Array.isArray(json) ? json.map(normalizeCustomer) : []);
      } catch {}
    }
    loadCustomers();
  }, [apiBase]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setCustOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    async function loadQuickBooks() {
      try {
        const token = localStorage.getItem('idToken');
        if (!token) {
          setQbStatus({ connected: false });
          setQbCustomers([]);
          return;
        }

        const res = await fetch(`${apiBase}/api/quickbooks/status`, {
          headers: {
            ...authHeaders(),
          },
        });

        if (res.status === 401) {
          setQbStatus({ connected: false });
          setQbCustomers([]);
          return;
        }

        const status = await res.json();
        setQbStatus(status);

        if (!status.connected) return;

        setQbLoading(true);

        const custRes = await fetch(`${apiBase}/api/quickbooks/customers`, {
          headers: {
            ...authHeaders(),
          },
        });

        const customers = await custRes.json();
        setQbCustomers(Array.isArray(customers) ? customers : []);
      } catch (err) {
        console.error('QuickBooks load failed', err);
      } finally {
        setQbLoading(false);
      }
    }

    loadQuickBooks();
  }, [apiBase]);

  useEffect(() => {
    async function loadSettings() {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) return;

        setTaxLoading(true);

        const res = await fetch(`${apiBase}/api/settings/${userId}`, {
          headers: authHeaders(),
        });

        if (!res.ok) throw new Error('Failed to load settings');

        const settings = await res.json();
        const normalizedRoles = Array.from(
          new Set(
            (Array.isArray(settings.team) ? settings.team : [])
              .map((member) => String(member?.role || '').trim())
              .filter(Boolean)
          )
        ).sort((a, b) => a.localeCompare(b));
        setTeamRoles(normalizedRoles);

        const taxLocations = Array.isArray(settings.taxLocations) ? settings.taxLocations : [];

        const normalizedTaxRates = taxLocations.map((t, idx) => {
          const numericRate =
            typeof t.tax === 'string' ? Number(t.tax.replace('%', '')) / 100 : Number(t.rate);

          return {
            id: t.id || `tax-${idx}`,
            name: `${t.state}${t.city ? ` – ${t.city}` : ''}`,
            rate: Number.isFinite(numericRate) ? numericRate : 0,
            raw: t,
          };
        });

        setTaxRates(normalizedTaxRates);

        // Auto-select default tax rate for NEW projects only
        if (!isEdit && !form.salesTaxRate) {
          const def = taxLocations.find((t) => t.isDefault);
          if (def) {
            setForm((f) => ({ ...f, salesTaxRate: def.id }));
          }
        }
      } catch (err) {
        console.error('Failed to load tax settings', err);
      } finally {
        setTaxLoading(false);
      }
    }

    loadSettings();
  }, [apiBase, isEdit]);

  function normalizeCustomer(c) {
    const created = c.customer || c;
    const secondary = created.secondary || {};
    return {
      id: created.id || created.customerId || '',
      name: created.name || created.customerName || '',
      email: created.email || '',
      secondaryEmail: created.secondaryEmail || secondary.email || '',
      phone: created.phone || '',
      secondaryPhone: created.secondaryPhone || secondary.phone || '',
      billingStreet: created.billingStreet || '',
      billingCity: created.billingCity || '',
      billingState: created.billingState || '',
      billingZip: created.billingZip || '',
      secondary,
      ...created,
    };
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      let next = { ...prev, [name]: value };

      // Keep project synced if "Same as Billing" is checked
      if (prev.projectSameAsBilling && name.startsWith('billing')) {
        if (name === 'billingStreet') next.projectStreet = value;
        if (name === 'billingStreet2') next.projectStreet2 = value;
        if (name === 'billingCity') next.projectCity = value;
        if (name === 'billingState') next.projectState = value;
        if (name === 'billingZip') next.projectZip = value;
      }

      return next;
    });

    if (status.error || status.success) setStatus({ loading: false, error: null, success: null });
  };

  const saveNewSalesTaxLocation = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) throw new Error('No userId found. Please sign in.');

    const city = String(newSalesTax.city || '').trim();
    const state = String(newSalesTax.state || '')
      .trim()
      .toUpperCase();
    const taxValue = Number(newSalesTax.tax);
    const agency = String(newSalesTax.agency || '').trim();
    const updated = String(newSalesTax.updated || '').trim();

    if (!city || !state || !agency || !updated || !Number.isFinite(taxValue) || taxValue < 0) {
      throw new Error('Complete all New Sales Tax fields with a valid non-negative rate.');
    }

    const nextEntry = {
      id: `tax-${Date.now()}`,
      city,
      state,
      tax: `${taxValue}%`,
      agency,
      updated,
    };

    const settingsRes = await fetch(`${apiBase}/api/settings/${encodeURIComponent(userId)}`, {
      headers: authHeaders(),
    });
    if (!settingsRes.ok) throw new Error('Failed to load current tax settings.');

    const settingsJson = await settingsRes.json().catch(() => ({}));
    const currentTaxLocations = Array.isArray(settingsJson.taxLocations)
      ? settingsJson.taxLocations
      : [];
    const updatedTaxLocations = [...currentTaxLocations, nextEntry];

    const saveRes = await fetch(`${apiBase}/api/settings/${encodeURIComponent(userId)}/tax`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taxLocations: updatedTaxLocations }),
    });

    if (!saveRes.ok) {
      const saveJson = await saveRes.json().catch(() => ({}));
      throw new Error(saveJson?.error || 'Failed to save new sales tax location.');
    }

    const parsedRate = Number.isFinite(taxValue) ? taxValue / 100 : 0;
    const displayName = `${state}${city ? ` - ${city}` : ''}`;
    const createdRate = { id: nextEntry.id, name: displayName, rate: parsedRate, raw: nextEntry };
    setTaxRates((prev) => [...prev, createdRate]);
    setNewSalesTax({ city: '', state: '', tax: '', agency: '', updated: '' });
    return nextEntry.id;
  };

  const saveNewQuickBooksCustomer = async () => {
    const trimmedName = String(newQbCustomerName || '').trim();
    if (!trimmedName) {
      throw new Error('Enter a name for the new QuickBooks customer.');
    }

    const res = await fetch(`${apiBase}/api/quickbooks/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ displayName: trimmedName }),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(json?.error || 'Failed to create QuickBooks customer.');
    }

    setQbCustomers((prev) => [...prev, json]);
    setNewQbCustomerName('');
    return json.Id;
  };

  const handleSelectCustomer = (cust) => {
    if (!cust) return;
    // Deliberately not auto-filling quickbooksCustomer from a name/email match here.
    // Each project gets its own QuickBooks sub-customer on sync (see backend
    // resolveCustomer); pre-linking to a matched customer would pin this new
    // project onto another project's existing QB customer/job instead. Users who
    // want to explicitly link to a specific existing QB customer can still pick
    // one from the "QuickBooks Customer" dropdown below.
    setForm((s) => ({
      ...s,
      customerId: cust.id,
      customerName: cust.name || '',
      email: cust.email || '',
      secondaryEmail: cust.secondaryEmail || cust.secondary?.email || '',
      phone: cust.phone || '',
      secondaryPhone: cust.secondaryPhone || cust.secondary?.phone || '',
      billingStreet: cust.billingStreet || '',
      billingStreet2: cust.billingStreet2 || '',
      billingCity: cust.billingCity || '',
      billingState: cust.billingState || '',
      billingZip: cust.billingZip || '',
    }));
    setCustOpen(false);
    setCustFilter('');
  };

  const createCustomerOnServer = async (payload) => {
    const userId = localStorage.getItem('userId') || '';
    const res = await fetch(`${apiBase}/api/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, userId }),
    });
    const body = await res.json().catch(() => ({}));
    return { status: res.status, body };
  };

  const submitCreateModal = async () => {
    setModalError(null);
    const {
      company,
      firstName,
      lastName,
      email,
      phone,
      billingStreet,
      billingCity,
      billingState,
      billingZip,
      secFirstName,
      secLastName,
      secEmail,
      secPhone,
      secBillingStreet,
      secBillingCity,
      secBillingState,
      secBillingZip,
    } = modalData;

    const name =
      company && company.trim()
        ? company.trim()
        : `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim();

    if (!name) {
      setModalError('Company or customer name is required.');
      return;
    }
    if (!email || !email.trim()) {
      setModalError('Email is required.');
      return;
    }

    setModalLoading(true);
    try {
      const payload = {
        name,
        company: company || '',
        firstName: firstName || '',
        lastName: lastName || '',
        email: (email || '').trim(),
        phone: phone || '',
        billingStreet: billingStreet || '',
        billingCity: billingCity || '',
        billingState: billingState || '',
        billingZip: billingZip || '',
        secondary: {
          firstName: secFirstName || '',
          lastName: secLastName || '',
          email: secEmail || '',
          phone: secPhone || '',
          billingStreet: secBillingStreet || '',
          billingCity: secBillingCity || '',
          billingState: secBillingState || '',
          billingZip: secBillingZip || '',
        },
      };

      const { status: code, body } = await createCustomerOnServer(payload);

      if (code === 201 && body.customer) {
        const created = normalizeCustomer(body.customer);
        setCustomers((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
        handleSelectCustomer(created);
        setShowCreateCustomer(false);
      } else if (code === 409 && body.customer) {
        const existing = normalizeCustomer(body.customer);
        setCustomers((prev) => [existing, ...prev.filter((p) => p.id !== existing.id)]);
        handleSelectCustomer(existing);
        setModalError('Customer already exists — selected existing.');
        setTimeout(() => setShowCreateCustomer(false), 700);
      } else {
        setModalError(body.error || `Server error (${code})`);
      }
    } catch (err) {
      const localCust = {
        id: `local-${Date.now()}`,
        name,
        email,
        phone,
        billingStreet,
        billingCity,
        billingState,
        billingZip,
        createdAt: new Date().toISOString(),
      };
      setCustomers((prev) => [localCust, ...prev]);
      handleSelectCustomer(localCust);
      setShowCreateCustomer(false);
    } finally {
      setModalLoading(false);
      setModalData({
        company: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        billingStreet: '',
        billingCity: '',
        billingState: '',
        billingZip: '',
        secFirstName: '',
        secLastName: '',
        secEmail: '',
        secPhone: '',
        secBillingStreet: '',
        secBillingCity: '',
        secBillingState: '',
        secBillingZip: '',
      });
    }
  };

  const filteredCustomers = customers.filter((c) =>
    (c.name || '').toLowerCase().includes((custFilter || '').toLowerCase())
  );

  const openCombobox = () => {
    setCustFilter(form.customerName || '');
    setCustOpen(true);
  };

  const getPresigned = async ({ filename, contentType, size }) => {
    const token = localStorage.getItem('idToken');
    const url = `${apiBase}/api/upload/presigned-url?filename=${encodeURIComponent(filename)}&type=${encodeURIComponent(contentType || 'application/octet-stream')}&size=${encodeURIComponent(size || 0)}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(txt || `Sign request failed (${res.status})`);
    }
    return res.json();
  };

  const resolveUploadedFileUrl = (sign) => {
    if (!sign || typeof sign !== 'object') return null;
    if (sign.fileUrl) return sign.fileUrl;
    if (sign.publicUrl) return sign.publicUrl;
    if (typeof sign.uploadUrl === 'string') {
      return sign.uploadUrl.split('?')[0] || null;
    }
    return null;
  };

  const onClickUpload = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const addDocumentIfNotExists = (prevDocs, newDoc) => {
    if (!newDoc) return prevDocs;
    const existsByUrl = newDoc.url && prevDocs.some((d) => d.url === newDoc.url);
    const existsByName = prevDocs.some((d) => d.name === newDoc.name && !newDoc.url);
    if (existsByUrl || existsByName) return prevDocs;
    return [newDoc, ...prevDocs];
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      const docId = uuidv4();
      setUploadingDocs((s) => [
        { id: docId, name: file.name, progress: 0, uploading: true, url: null, error: null },
        ...s,
      ]);

      try {
        const sign = await getPresigned({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        });
        const putUrl = sign.uploadUrl || sign.url || sign.presignedUrl;
        if (!putUrl) throw new Error('Invalid signing response');

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', putUrl, true);
          if (file.type) xhr.setRequestHeader('Content-Type', file.type);

          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) {
              const p = Math.round((ev.loaded / ev.total) * 100);
              setUploadingDocs((list) =>
                list.map((d) => (d.id === docId ? { ...d, progress: p } : d))
              );
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload failed (${xhr.status})`));
          };
          xhr.onerror = () => reject(new Error('Network error during upload'));
          xhr.send(file);
        });

        const publicUrl = resolveUploadedFileUrl(sign);
        setForm((prev) => ({
          ...prev,
          documents: addDocumentIfNotExists(prev.documents, { name: file.name, url: publicUrl }),
        }));
        setUploadingDocs((list) => list.filter((d) => d.id !== docId));
      } catch (err) {
        setUploadingDocs((list) =>
          list.map((d) => (d.id === docId ? { ...d, uploading: false, error: err.message } : d))
        );
        console.error('Upload error', err);
      }
    }

    e.target.value = '';
  };

  const handleCoverUpload = () => {
    if (coverInputRef.current) coverInputRef.current.click();
  };

  useEffect(() => {
    if (!location.state?.openCoverUpload) return;
    setShowCoverPrompt(true);
    const t = setTimeout(() => {
      coverUploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      handleCoverUpload();
    }, 0);
    navigate(location.pathname, {
      replace: true,
      state: { project: existingProject || null },
    });
    return () => clearTimeout(t);
  }, [location.state?.openCoverUpload, location.pathname, navigate, existingProject]);

  const uploadCoverFile = async (file) => {
    if (!file) return;

    const validation = validateWebImageFile(file);
    if (!validation.ok) {
      setUploadingCover({
        id: uuidv4(),
        name: file.name,
        progress: 0,
        uploading: false,
        url: null,
        error: validation.error,
      });
      return;
    }

    const id = uuidv4();
    setUploadingCover({
      id,
      name: file.name,
      progress: 0,
      uploading: true,
      url: null,
      error: null,
    });

    try {
      const sign = await getPresigned({
        filename: file.name,
        contentType: validation.mime,
        size: file.size,
      });
      const putUrl = sign.uploadUrl || sign.url || sign.presignedUrl;
      if (!putUrl) throw new Error('Invalid signing response');

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', putUrl, true);
        xhr.setRequestHeader('Content-Type', validation.mime);

        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const p = Math.round((ev.loaded / ev.total) * 100);
            setUploadingCover((s) => (s ? { ...s, progress: p } : { id, progress: p }));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(file);
      });

      const publicUrl = resolveUploadedFileUrl(sign);
      setUploadingCover({ id, name: file.name, progress: 100, uploading: false, url: publicUrl });
      setForm((f) => ({ ...f, coverImageUrl: publicUrl }));
    } catch (err) {
      setUploadingCover((s) =>
        s
          ? { ...s, uploading: false, error: err.message }
          : { id, uploading: false, error: err.message }
      );
      console.error('Cover upload error', err);
    }
  };

  const handleCoverFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) await uploadCoverFile(file);
    e.target.value = '';
  };

  const handleCoverDrop = async (e) => {
    e.preventDefault();
    setCoverDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await uploadCoverFile(file);
  };

  const removeDocument = (idOrUrl) => {
    setUploadingDocs((s) => s.filter((d) => d.id !== idOrUrl && d.url !== idOrUrl));
    setForm((f) => ({
      ...f,
      documents: f.documents.filter((doc) => doc.url !== idOrUrl && doc.name !== idOrUrl),
    }));
  };

  const updateRoleRate = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      timeBillingRoleRates: prev.timeBillingRoleRates.map((entry, currentIndex) =>
        currentIndex === index ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const addRoleRateRow = () => {
    setForm((prev) => ({
      ...prev,
      timeBillingRoleRates: [...prev.timeBillingRoleRates, { role: '', rate: '' }],
    }));
  };

  const removeRoleRateRow = (index) => {
    setForm((prev) => ({
      ...prev,
      timeBillingRoleRates: prev.timeBillingRoleRates.filter(
        (_, currentIndex) => currentIndex !== index
      ),
    }));
  };

  const handleSave = async () => {
    if (!form.projectNickname || !form.projectNickname.trim()) {
      setStatus({ loading: false, error: 'Project nickname is required.', success: null });
      return;
    }

    const userId = localStorage.getItem('userId');
    if (!userId) {
      setStatus({ loading: false, error: 'No userId found. Please sign in.', success: null });
      return;
    }

    setStatus({ loading: true, error: null, success: null });

    try {
      let resolvedSalesTaxRate = form.salesTaxRate;
      if (form.salesTaxRate === NEW_SALES_TAX_VALUE) {
        setSavingNewSalesTax(true);
        resolvedSalesTaxRate = await saveNewSalesTaxLocation();
      }

      let resolvedQuickbooksCustomer = form.quickbooksCustomer;
      if (form.quickbooksCustomer === NEW_QB_CUSTOMER_VALUE) {
        setSavingNewQbCustomer(true);
        resolvedQuickbooksCustomer = await saveNewQuickBooksCustomer();
      }

      const payload = {
        ...form,
        salesTaxRate: resolvedSalesTaxRate,
        quickbooksCustomer: resolvedQuickbooksCustomer,
        userId,
        projectId,
        tags,
        timeBillingRoleRates: normalizeRoleRateEntries(form.timeBillingRoleRates).filter(
          (entry) => entry.role
        ),
      };

      const url = isEdit
        ? `${apiBase}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`
        : `${apiBase}/api/projects`;

      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setStatus({
          loading: false,
          error: json?.error || `Server error (${res.status})`,
          success: null,
        });
        return;
      }

      const nextProjectId = isEdit ? projectId : json.projectId;
      if (!nextProjectId) {
        setStatus({
          loading: false,
          error: 'Project saved, but no projectId returned from server.',
          success: null,
        });
        return;
      }

      navigate(`/projects/${encodeURIComponent(nextProjectId)}`, {
        replace: true,
        state: { refresh: true },
      });
    } catch (err) {
      console.error(err);
      setStatus({ loading: false, error: err?.message || 'Network or server error.', success: null });
    } finally {
      setSavingNewSalesTax(false);
      setSavingNewQbCustomer(false);
    }
  };

  const handleCancel = () => {
    setForm({ ...initialForm });
    setStatus({ loading: false, error: null, success: null });
    setUploadingDocs([]);
    setUploadingCover(null);
    navigate('/dashboard');
  };

  // Same display-name fallback the projects list and reports use, so the name
  // the user is asked to type matches the one they see everywhere else.
  const projectDisplayName =
    form.projectNickname?.trim() || form.customerName?.trim() || 'Untitled Project';

  const handleDelete = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      setDeleteError('No userId found. Please sign in.');
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      const res = await fetch(
        `${apiBase}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
        { method: 'DELETE', headers: authHeaders() }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Delete failed (HTTP ${res.status}).`);
      }

      setShowDeleteModal(false);
      navigate('/dashboard', { replace: true, state: { refresh: true } });
    } catch (err) {
      console.error('Project delete failed:', err);
      setDeleteError(err?.message || 'Network or server error.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="new-project-page">
      <div className="page-inner">
        <div className="np-page-header">
          <div className="np-title-stack">
            <h1 className="page-title">Project Information</h1>
            <div className="page-subtitle">
              Capture contacts, addresses, scope, and documents for this project.
            </div>
          </div>

          {isEdit && (
            <div className="project-active-toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                <span className="toggle-switch" />
                <span className="toggle-status">{form.isActive ? 'Active' : 'Inactive'}</span>
              </label>
            </div>
          )}
        </div>

        <section className="np-section">
          <div className="np-section-head">
            <h2 className="section-label">General Information</h2>
            <div className="np-divider" />
          </div>

          <div className="grid-two">
            <div className="left-col">
              <input
                name="projectNickname"
                placeholder="Project Nickname"
                value={form.projectNickname}
                onChange={handleChange}
              />

              <div ref={containerRef} style={{ position: 'relative' }}>
                <label className="sr-only" htmlFor="customer-combobox">
                  Customer
                </label>
                <div
                  className="customer-combobox"
                  role="combobox"
                  aria-expanded={custOpen}
                  aria-haspopup="listbox"
                >
                  <input
                    id="customer-combobox"
                    placeholder="Customer First and Last Name"
                    value={custOpen ? custFilter : form.customerName}
                    onChange={(e) => {
                      setCustFilter(e.target.value);
                      setCustOpen(true);
                    }}
                    onFocus={openCombobox}
                    onClick={openCombobox}
                  />
                  <button
                    type="button"
                    aria-label="Toggle customer list"
                    className="combobox-toggle"
                    onClick={() => (custOpen ? setCustOpen(false) : openCombobox())}
                  >
                    ▾
                  </button>
                </div>

                {custOpen && (
                  <div className="combobox-panel" role="listbox" aria-label="Customer suggestions">
                    <div className="combobox-search" role="search">
                      <input
                        className="combobox-search-input"
                        placeholder="Search customers..."
                        value={custFilter}
                        onChange={(e) => setCustFilter(e.target.value)}
                        autoFocus
                        aria-label="Search customers"
                      />
                      <button
                        type="button"
                        className="combobox-search-clear"
                        onClick={() => setCustFilter('')}
                        aria-label="Clear search"
                      >
                        ✕
                      </button>
                    </div>

                    <ul className="combobox-list" role="presentation">
                      <li
                        key="add-new"
                        className="combobox-add"
                        onClick={() => {
                          setShowCreateCustomer(true);
                          setCustOpen(false);
                        }}
                        onKeyDown={(e) =>
                          (e.key === 'Enter' || e.key === ' ') &&
                          (setShowCreateCustomer(true), setCustOpen(false))
                        }
                        tabIndex={0}
                        role="option"
                      >
                        <span className="add-icon">＋</span>
                        <span className="add-text">Add New Customer</span>
                      </li>

                      {filteredCustomers.length === 0 && (
                        <li className="combobox-empty">No customers found</li>
                      )}

                      {filteredCustomers.map((c) => (
                        <li
                          key={c.id}
                          className={`combobox-item ${form.customerId === c.id ? 'selected' : ''}`}
                          onClick={() => handleSelectCustomer(c)}
                          onKeyDown={(e) =>
                            (e.key === 'Enter' || e.key === ' ') && handleSelectCustomer(c)
                          }
                          tabIndex={0}
                          role="option"
                          aria-selected={form.customerId === c.id}
                        >
                          <div className="cust-left">
                            <div className="cust-name">{c.name}</div>
                            <div className="cust-meta">
                              {c.email || ''}
                              {c.phone ? ` • ${c.phone}` : ''}
                            </div>
                          </div>
                          <div className="cust-right">›</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <input name="email" placeholder="Email" value={form.email} onChange={handleChange} />
              <input
                name="secondaryEmail"
                placeholder="Secondary Email"
                value={form.secondaryEmail}
                onChange={handleChange}
              />
              <input
                name="phone"
                placeholder="Phone Number"
                value={form.phone}
                onChange={handleChange}
              />
              <input
                name="secondaryPhone"
                placeholder="Secondary Phone Number"
                value={form.secondaryPhone}
                onChange={handleChange}
              />
            </div>

            <div className="right-col">
              <label className="alternative-label">Billing Address</label>
              <AddressAutocomplete
                name="billingStreet"
                placeholder="Street Address"
                value={form.billingStreet}
                onChange={handleChange}
                onSelect={({ street, city, state, zip }) =>
                  setForm((prev) => ({
                    ...prev,
                    billingStreet: street,
                    billingCity: city,
                    billingState: state,
                    billingZip: zip,
                    ...(prev.projectSameAsBilling && {
                      projectStreet: street,
                      projectCity: city,
                      projectState: state,
                      projectZip: zip,
                    }),
                  }))
                }
              />
              <input
                name="billingStreet2"
                placeholder="Suite, Apt, Unit #"
                value={form.billingStreet2}
                onChange={handleChange}
              />
              <div className="row-split address-row">
                <input
                  name="billingCity"
                  placeholder="City"
                  value={form.billingCity}
                  onChange={handleChange}
                />
                <div className="select-shell">
                  <select name="billingState" value={form.billingState} onChange={handleChange}>
                    <option value="">State</option>
                    {US_STATE_OPTIONS.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <span className="select-shell-arrow" aria-hidden="true">
                    ▾
                  </span>
                </div>
                <input
                  name="billingZip"
                  placeholder="Zip Code"
                  value={form.billingZip}
                  onChange={handleChange}
                />
              </div>

              <div className="address-header-row">
                <label className="alternative-label">Project Address</label>
                <label className="sync-checkbox-inline">
                  <input
                    type="checkbox"
                    checked={form.projectSameAsBilling || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => {
                        if (checked) {
                          return {
                            ...prev,
                            projectSameAsBilling: true,
                            projectStreet: prev.billingStreet,
                            projectStreet2: prev.billingStreet2,
                            projectCity: prev.billingCity,
                            projectState: prev.billingState,
                            projectZip: prev.billingZip,
                          };
                        } else {
                          return {
                            ...prev,
                            projectSameAsBilling: false,
                            projectStreet: '',
                            projectStreet2: '',
                            projectCity: '',
                            projectState: '',
                            projectZip: '',
                          };
                        }
                      });
                    }}
                  />
                  <span>Same as Billing Address</span>
                </label>
              </div>

              <AddressAutocomplete
                name="projectStreet"
                placeholder="Street Address"
                value={form.projectStreet}
                onChange={handleChange}
                disabled={form.projectSameAsBilling}
                onSelect={({ street, city, state, zip }) =>
                  setForm((prev) => ({
                    ...prev,
                    projectStreet: street,
                    projectCity: city,
                    projectState: state,
                    projectZip: zip,
                  }))
                }
              />
              <input
                name="projectStreet2"
                placeholder="Suite, Apt, Unit #"
                value={form.projectStreet2}
                onChange={handleChange}
                disabled={form.projectSameAsBilling}
              />
              <div className="row-split address-row">
                <input
                  name="projectCity"
                  placeholder="City"
                  value={form.projectCity}
                  onChange={handleChange}
                  disabled={form.projectSameAsBilling}
                />
                <div className="select-shell">
                  <select
                    name="projectState"
                    value={form.projectState}
                    onChange={handleChange}
                    disabled={form.projectSameAsBilling}
                  >
                    <option value="">State</option>
                    {US_STATE_OPTIONS.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <span className="select-shell-arrow" aria-hidden="true">
                    ▾
                  </span>
                </div>
                <input
                  name="projectZip"
                  placeholder="Zip Code"
                  value={form.projectZip}
                  onChange={handleChange}
                  disabled={form.projectSameAsBilling}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="np-section">
          <div className="np-section-head">
            <h2 className="section-label">Project Scope</h2>
            <div className="np-divider" />
          </div>

          <div className="grid-two">
            <div className="left-col">
              <textarea
                name="notes"
                className="large-textarea"
                placeholder="Project Notes"
                value={form.notes}
                onChange={handleChange}
              />

              <div style={{ marginTop: '12px' }}>
                <label
                  className="field-label"
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  Tags
                </label>
                <TagSelector selected={tags} onChange={setTags} />
              </div>

              <label
                className="upload-card cover-upload"
                ref={coverUploadRef}
                htmlFor={coverInputId}
                onDragOver={(e) => {
                  e.preventDefault();
                  setCoverDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setCoverDragActive(false);
                }}
                onDrop={handleCoverDrop}
                style={{
                  display: 'block',
                  cursor: 'pointer',
                  outline: coverDragActive ? '2px dashed #7ca65c' : 'none',
                  outlineOffset: 4,
                }}
              >
                <div className="np-cover-flex">
                  <div className="np-upload-placeholder">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <path
                        d="M12 3v10"
                        stroke="#0fb48b"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 7l4-4 4 4"
                        stroke="#0fb48b"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <rect
                        x="3"
                        y="11"
                        width="18"
                        height="9"
                        rx="2"
                        stroke="#d6d6d6"
                        strokeWidth="1"
                        fill="transparent"
                      />
                    </svg>
                    <div className="np-upload-text">Project Cover Image</div>
                    <div className="np-upload-link">Search Image Library</div>
                    {showCoverPrompt && !uploadingCover && !form.coverImageUrl && (
                      <div className="np-upload-hint">Click here to choose a cover image.</div>
                    )}

                    <div style={{ marginTop: 8 }}>
                      {uploadingCover && uploadingCover.uploading && (
                        <div>Uploading {uploadingCover.progress}%</div>
                      )}
                      {uploadingCover && !uploadingCover.uploading && uploadingCover.error && (
                        <div className="np-upload-error">{uploadingCover.error}</div>
                      )}
                      {!uploadingCover && !form.coverImageUrl && (
                        <div className="np-upload-hint">Click to upload a cover image</div>
                      )}
                    </div>
                  </div>

                  <div className="np-cover-preview">
                    {uploadingCover && uploadingCover.uploading && (
                      <div className="np-preview-placeholder">
                        <div>Uploading… {uploadingCover.progress}%</div>
                      </div>
                    )}

                    {uploadingCover && !uploadingCover.uploading && uploadingCover.url && (
                      <img src={uploadingCover.url} alt="Cover preview" className="np-cover-img" />
                    )}

                    {!uploadingCover && form.coverImageUrl && (
                      <img src={form.coverImageUrl} alt="Cover preview" className="np-cover-img" />
                    )}

                    {!uploadingCover && !form.coverImageUrl && (
                      <div className="np-preview-empty">No cover image</div>
                    )}
                  </div>
                </div>
              </label>

              <input
                ref={coverInputRef}
                id={coverInputId}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.svg,image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml"
                style={VISUALLY_HIDDEN_FILE_INPUT_STYLE}
                onChange={handleCoverFileChange}
              />

              <div style={{ marginTop: '16px' }}>
                <label
                  className="field-label"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '6px',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#E60023" aria-hidden>
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                  </svg>
                  Pinterest Mood Board
                </label>
                <input
                  name="pinterestBoardUrl"
                  type="url"
                  placeholder="https://pinterest.com/yourboard/..."
                  value={form.pinterestBoardUrl}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="right-col">
              <label className="section-label">Receiving Warehouse</label>
              <input
                name="receivingCompany"
                placeholder="Company Name"
                value={form.receivingCompany}
                onChange={handleChange}
              />
              <AddressAutocomplete
                name="receivingStreet"
                placeholder="Street Address"
                value={form.receivingStreet}
                onChange={handleChange}
                onSelect={({ street, city, state, zip }) =>
                  setForm((prev) => ({
                    ...prev,
                    receivingStreet: street,
                    receivingCity: city,
                    receivingState: state,
                    receivingZip: zip,
                  }))
                }
              />
              <div className="row-split address-row">
                <input
                  name="receivingCity"
                  placeholder="City"
                  value={form.receivingCity}
                  onChange={handleChange}
                />
                <div className="select-shell">
                  <select name="receivingState" value={form.receivingState} onChange={handleChange}>
                    <option value="">State</option>
                    {US_STATE_OPTIONS.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <span className="select-shell-arrow" aria-hidden="true">
                    ▾
                  </span>
                </div>
                <input
                  name="receivingZip"
                  placeholder="Zip Code"
                  value={form.receivingZip}
                  onChange={handleChange}
                />
              </div>
              <input
                name="receivingEmail"
                placeholder="Email"
                value={form.receivingEmail}
                onChange={handleChange}
              />
              <input
                name="receivingPhone"
                placeholder="Phone Number"
                value={form.receivingPhone}
                onChange={handleChange}
              />
              <input className="spacer-input" disabled />
            </div>
          </div>
        </section>

        <section className="np-section">
          <div className="np-section-head">
            <h2 className="section-label">Folio</h2>
            <div className="np-divider" />
          </div>

          <div className="folio-row">
            <div
              className="doc-upload upload-area"
              onClick={onClickUpload}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClickUpload()}
            >
              <img src="/folder-icon.png" alt="folder" className="folder-icon-img" />
              <div className="doc-input upload-inner">
                <div className="doc-placeholder">
                  {form.documents[0] ? form.documents[0].name : 'Upload Document'}
                </div>
                <button
                  type="button"
                  className="upload-inside-btn"
                  aria-label="Upload document"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickUpload();
                  }}
                >
                  ⤓
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/*,application/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                multiple
              />
            </div>

            <div
              className="doc-upload upload-area"
              onClick={onClickUpload}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClickUpload()}
            >
              <img src="/folder-icon.png" alt="folder" className="folder-icon-img" />
              <div className="doc-input upload-inner">
                <div className="doc-placeholder">Upload Document</div>
                <button
                  type="button"
                  className="upload-inside-btn"
                  aria-label="Upload document"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickUpload();
                  }}
                >
                  ⤓
                </button>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            {uploadingDocs.map((d) => (
              <div key={d.id} className="uploaded-row">
                <div className="uploaded-meta">
                  <strong style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {d.name}
                  </strong>
                  {d.uploading ? (
                    <div className="upload-progress">
                      <div className="upload-track">
                        <div className="upload-bar" style={{ width: `${d.progress}%` }} />
                      </div>
                      <span className="upload-percent">{d.progress}%</span>
                    </div>
                  ) : d.url ? (
                    <a href={d.url} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  ) : (
                    <span className="upload-error">{d.error}</span>
                  )}
                </div>
                <button className="mini-remove" onClick={() => removeDocument(d.id)}>
                  ✕
                </button>
              </div>
            ))}

            {form.documents.map((doc) => (
              <div key={doc.url || doc.name} className="uploaded-row">
                <div className="uploaded-meta">
                  <strong style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.name}
                  </strong>
                  <span> — </span>
                  {doc.url ? (
                    <a href={doc.url} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  ) : (
                    <span>Local</span>
                  )}
                </div>
                <button className="mini-remove" onClick={() => removeDocument(doc.url || doc.name)}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="np-section">
          <div className="np-section-head">
            <h2 className="section-label">Project Settings</h2>
            <div className="np-divider" />
          </div>

          <div className="grid-two settings-grid">
            <div className="left-col settings-left">
              <label className="small-label">QuickBooks Customer</label>

              {!qbStatus.connected && (
                <div className="qb-hint">
                  Connect QuickBooks in Settings to enable customer mapping.
                </div>
              )}

              {qbStatus.connected && (
                <select
                  name="quickbooksCustomer"
                  value={form.quickbooksCustomer || ''}
                  onChange={handleChange}
                  disabled={qbLoading}
                >
                  <option value="">
                    {qbLoading ? 'Loading customers…' : 'Select QuickBooks customer'}
                  </option>
                  <option value={NEW_QB_CUSTOMER_VALUE}>+ Add New Customer</option>

                  {qbCustomers.map((c) => (
                    <option key={c.Id} value={c.Id}>
                      {c.DisplayName}
                    </option>
                  ))}
                </select>
              )}

              {qbStatus.connected && form.quickbooksCustomer === NEW_QB_CUSTOMER_VALUE && (
                <input
                  style={{ marginTop: 8 }}
                  placeholder="New QuickBooks Customer Name"
                  value={newQbCustomerName}
                  onChange={(e) => setNewQbCustomerName(e.target.value)}
                />
              )}

              <input
                name="paymentMethod"
                value={form.paymentMethod}
                onChange={handleChange}
                placeholder="Payment Method"
              />
              <input
                name="defaultMarkup"
                value={form.defaultMarkup}
                onChange={handleChange}
                placeholder="Default Markup Rate"
              />
              <label className="small-label">Sales Tax Rate</label>

              <select name="salesTaxRate" value={form.salesTaxRate || ''} onChange={handleChange}>
                <option value="">Select Sales Tax Rate</option>
                <option value={NEW_SALES_TAX_VALUE}>+ New Sales Tax</option>

                {taxRates.map((tax) => (
                  <option key={tax.id} value={tax.id}>
                    {tax.name} ({(tax.rate * 100).toFixed(2)}%)
                  </option>
                ))}
              </select>

              {form.salesTaxRate === NEW_SALES_TAX_VALUE && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                  <input
                    placeholder="City"
                    value={newSalesTax.city}
                    onChange={(e) => setNewSalesTax((prev) => ({ ...prev, city: e.target.value }))}
                  />
                  <input
                    placeholder="State (e.g. NY)"
                    value={newSalesTax.state}
                    onChange={(e) => setNewSalesTax((prev) => ({ ...prev, state: e.target.value }))}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Sales Tax %"
                    value={newSalesTax.tax}
                    onChange={(e) => setNewSalesTax((prev) => ({ ...prev, tax: e.target.value }))}
                  />
                  <input
                    type="date"
                    value={newSalesTax.updated}
                    onChange={(e) => setNewSalesTax((prev) => ({ ...prev, updated: e.target.value }))}
                  />
                  <input
                    style={{ gridColumn: '1 / -1' }}
                    placeholder="Agency"
                    value={newSalesTax.agency}
                    onChange={(e) => setNewSalesTax((prev) => ({ ...prev, agency: e.target.value }))}
                  />
                </div>
              )}

              <div className="np-subsection-head">
                <label className="small-label" style={{ marginBottom: 0 }}>
                  Time Billing Role Rates
                </label>
                <button type="button" className="np-inline-add-btn" onClick={addRoleRateRow}>
                  + Add Role Rate
                </button>
              </div>

              <div className="inline-hint" style={{ marginBottom: 10 }}>
                Set a default client rate for each role on this project.
              </div>

              {form.timeBillingRoleRates.length === 0 ? (
                <div className="np-empty-note">
                  No project role rates set. Time billing will keep the client rate blank by
                  default.
                </div>
              ) : (
                <div className="np-role-rate-list">
                  {form.timeBillingRoleRates.map((entry, index) => (
                    <div className="np-role-rate-row" key={`role-rate-${index}`}>
                      <input
                        list="project-role-options"
                        placeholder="Role"
                        value={entry.role}
                        onChange={(e) => updateRoleRate(index, 'role', e.target.value)}
                      />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Client Rate"
                        value={entry.rate}
                        onChange={(e) => updateRoleRate(index, 'rate', e.target.value)}
                      />
                      <button
                        type="button"
                        className="mini-remove np-role-rate-remove"
                        onClick={() => removeRoleRateRow(index)}
                        aria-label="Remove role rate"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <datalist id="project-role-options">
                {teamRoles.map((role) => (
                  <option key={role} value={role} />
                ))}
              </datalist>

              <div className="inline-hint">
                Manage tax rates in <strong>Settings → Tax</strong>
              </div>
            </div>
          </div>
        </section>

        {isEdit && (
          <section className="np-section np-danger-zone">
            <div className="np-section-head">
              <h2 className="section-label">Danger Zone</h2>
              <div className="np-divider" />
            </div>

            <div className="np-danger-row">
              <div className="np-danger-copy">
                <div className="np-danger-title">Delete this project</div>
                <div className="np-danger-desc">
                  Permanently removes the project and everything filed under it — proposals,
                  invoices, purchase orders, retainers, credits, library items, files, schedule
                  items, time entries, to-dos and chat. This cannot be undone. To keep the
                  records but hide the project, switch it to Inactive instead.
                </div>
              </div>
              <button
                className="np-btn-danger"
                type="button"
                onClick={() => {
                  setDeleteConfirmText('');
                  setDeleteError(null);
                  setShowDeleteModal(true);
                }}
              >
                Delete Project
              </button>
            </div>
          </section>
        )}

        <div className="np-footer-spacer" />
      </div>

      <div className="action-buttons" role="group" aria-label="Project actions">
        <button
          className="np-btn-save"
          onClick={handleSave}
          disabled={status.loading || savingNewSalesTax || savingNewQbCustomer}
          type="button"
        >
          {status.loading || savingNewSalesTax || savingNewQbCustomer ? 'Saving…' : 'Save'}
        </button>
        <button className="np-btn-cancel" onClick={handleCancel} type="button">
          Cancel
        </button>
      </div>

      <div role="status" aria-live="polite" style={{ padding: '0.5rem 1rem', minHeight: 28 }}>
        {status.error && <div className="status-error">{status.error}</div>}
        {status.success && <div className="status-success">{status.success}</div>}
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => !deleting && setShowDeleteModal(false)}
        title="Delete Project"
      >
        <p className="np-danger-desc">
          This permanently deletes <strong>{projectDisplayName}</strong> and every proposal,
          invoice, purchase order, retainer, credit, library item, file, schedule item, time
          entry, to-do and chat message attached to it. There is no undo.
        </p>

        <label className="np-danger-label" htmlFor="delete-confirm-input">
          Type <strong>{projectDisplayName}</strong> to confirm:
        </label>
        <input
          id="delete-confirm-input"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          placeholder={projectDisplayName}
          autoComplete="off"
          disabled={deleting}
        />

        {deleteError && <div className="status-error">{deleteError}</div>}

        <div className="np-modal-actions">
          <button
            className="np-btn-danger"
            type="button"
            onClick={handleDelete}
            disabled={deleting || deleteConfirmText.trim() !== projectDisplayName}
          >
            {deleting ? 'Deleting…' : 'Delete Forever'}
          </button>
          <button
            className="np-btn-cancel"
            type="button"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showCreateCustomer}
        onClose={() => setShowCreateCustomer(false)}
        title="New Customer"
      >
        {/* PRIMARY CONTACT */}
        <div className="np-section-title">Primary Contact</div>

        <div className="np-grid-3">
          <input
            placeholder="Company"
            value={modalData.company}
            onChange={(e) => setModalData({ ...modalData, company: e.target.value })}
          />

          <input
            placeholder="First Name"
            value={modalData.firstName}
            onChange={(e) => setModalData({ ...modalData, firstName: e.target.value })}
          />

          <input
            placeholder="Last Name"
            value={modalData.lastName}
            onChange={(e) => setModalData({ ...modalData, lastName: e.target.value })}
          />

          <input
            placeholder="Email Address"
            className="np-full-row"
            value={modalData.email}
            onChange={(e) => setModalData({ ...modalData, email: e.target.value })}
          />

          <input
            placeholder="Phone Number"
            value={modalData.phone}
            onChange={(e) => setModalData({ ...modalData, phone: e.target.value })}
          />

          <AddressAutocomplete
            placeholder="Street Address"
            className="np-full-row"
            value={modalData.billingStreet}
            onChange={(e) => setModalData({ ...modalData, billingStreet: e.target.value })}
            onSelect={({ street, city, state, zip }) =>
              setModalData((prev) => ({
                ...prev,
                billingStreet: street,
                billingCity: city,
                billingState: state,
                billingZip: zip,
              }))
            }
          />

          <input
            placeholder="City"
            value={modalData.billingCity}
            onChange={(e) => setModalData({ ...modalData, billingCity: e.target.value })}
          />

          <input
            placeholder="State"
            value={modalData.billingState}
            onChange={(e) => setModalData({ ...modalData, billingState: e.target.value })}
          />

          <input
            placeholder="Zip"
            value={modalData.billingZip}
            onChange={(e) => setModalData({ ...modalData, billingZip: e.target.value })}
          />
        </div>

        {/* SECONDARY CONTACT */}
        <div className="np-section-title">Secondary Contact</div>

        <div className="np-grid-3">
          <input
            placeholder="First Name"
            value={modalData.secFirstName}
            onChange={(e) => setModalData({ ...modalData, secFirstName: e.target.value })}
          />

          <input
            placeholder="Last Name"
            value={modalData.secLastName}
            onChange={(e) => setModalData({ ...modalData, secLastName: e.target.value })}
          />

          <input
            placeholder="Email Address"
            value={modalData.secEmail}
            onChange={(e) => setModalData({ ...modalData, secEmail: e.target.value })}
          />

          <input
            placeholder="Phone Number"
            value={modalData.secPhone}
            onChange={(e) => setModalData({ ...modalData, secPhone: e.target.value })}
          />

          <AddressAutocomplete
            placeholder="Street Address"
            className="np-full-row"
            value={modalData.secBillingStreet}
            onChange={(e) => setModalData({ ...modalData, secBillingStreet: e.target.value })}
            onSelect={({ street, city, state, zip }) =>
              setModalData((prev) => ({
                ...prev,
                secBillingStreet: street,
                secBillingCity: city,
                secBillingState: state,
                secBillingZip: zip,
              }))
            }
          />

          <input
            placeholder="City"
            value={modalData.secBillingCity}
            onChange={(e) => setModalData({ ...modalData, secBillingCity: e.target.value })}
          />

          <input
            placeholder="State"
            value={modalData.secBillingState}
            onChange={(e) => setModalData({ ...modalData, secBillingState: e.target.value })}
          />

          <input
            placeholder="Zip"
            value={modalData.secBillingZip}
            onChange={(e) => setModalData({ ...modalData, secBillingZip: e.target.value })}
          />
        </div>

        <div className="np-modal-footer">
          <button className="np-btn-save" onClick={submitCreateModal}>
            {modalLoading ? 'Saving…' : 'Save'}
          </button>
          <button className="np-btn-cancel" onClick={() => setShowCreateCustomer(false)}>
            Cancel
          </button>
        </div>

        {modalError && <div className="modal-error">{modalError}</div>}
      </Modal>
    </div>
  );
}
